/**
 * OperatingModeToggle — Sprint 1 · Project Symphony.
 *
 * Two-card selector for the workspace operating mode.
 * - **Manual** (default): full V1 behavior.
 * - **Autopilot**: Symphony orchestration mode. Downstream features
 *   are unwired at Sprint 1; the flag simply persists.
 *
 * Accessibility: uses ``role="radiogroup"`` with two ``role="radio"``
 * cards, keyboard-navigable via arrow keys (browser default with
 * `<button>` + role wiring).
 */
import { Loader2 } from "lucide-react";

import { Card } from "@/shared/ui/card";
import { toast } from "sonner";
import { useSetOperatingMode } from "@/entities/workspace/hooks";
import type { OperatingMode } from "@/entities/workspace/api";
import type { Workspace } from "@/entities/workspace/model";
import { cn } from "@/shared/lib/utils";

interface Props {
  workspace: Workspace;
}

export function OperatingModeToggle({ workspace }: Props) {
  const current = workspace.settings.operating_mode;
  const mutation = useSetOperatingMode(workspace.id);

  const handleSelect = (mode: OperatingMode) => {
    if (mode === current || mutation.isPending) return;
    mutation.mutate(mode, {
      onSuccess: (updated) => {
        toast.success(
          updated.settings.operating_mode === "autopilot"
            ? "Autopilot Mode enabled"
            : "Back to Manual Mode",
          {
            description:
              updated.settings.operating_mode === "autopilot"
                ? "Orkexra will start building campaigns from your goals in upcoming sprints."
                : "V1 workflows continue exactly as before.",
          },
        );
      },
      onError: () => {
        toast.error("Couldn't change operating mode", {
          description: "Please try again in a moment.",
        });
      },
    });
  };

  return (
    <section
      className="space-y-4"
      aria-labelledby="operating-mode-heading"
      data-testid="operating-mode-section"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3
            id="operating-mode-heading"
            className="text-lg font-semibold tracking-tight"
          >
            Workspace mode
          </h3>
          <p className="text-sm text-muted-foreground">
            Choose how Orkexra works for this workspace. You can switch
            back and forth any time — nothing is deleted.
          </p>
        </div>
        {mutation.isPending && (
          <Loader2
            className="h-4 w-4 animate-spin text-muted-foreground"
            data-testid="operating-mode-loading"
            aria-hidden
          />
        )}
      </div>

      <div
        role="radiogroup"
        aria-labelledby="operating-mode-heading"
        className="grid gap-4 sm:grid-cols-2"
      >
        <ModeCard
          mode="manual"
          title="Manual"
          tagline="Create assets individually."
          body="Full V1 experience. You open AI Studio, choose what to make, and Orkexra assists at every step."
          active={current === "manual"}
          disabled={mutation.isPending}
          onSelect={() => handleSelect("manual")}
        />
        <ModeCard
          mode="autopilot"
          title="Autopilot"
          tagline="Tell Orkexra your business goal. We'll build the campaign."
          body="Answer six questions and Orkexra designs, plans and drafts your marketing. You review and approve — nothing publishes without you."
          active={current === "autopilot"}
          disabled={mutation.isPending}
          onSelect={() => handleSelect("autopilot")}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        <strong>Current:</strong>{" "}
        <span data-testid="operating-mode-current">{current}</span>
      </p>
    </section>
  );
}

interface ModeCardProps {
  mode: OperatingMode;
  title: string;
  tagline: string;
  body: string;
  active: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function ModeCard({ mode, title, tagline, body, active, disabled, onSelect }: ModeCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onSelect}
      data-testid={`operating-mode-card-${mode}`}
      data-active={active}
      className={cn(
        "text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg",
        disabled && "opacity-70 cursor-not-allowed",
      )}
    >
      <Card
        className={cn(
          "p-5 h-full border-2 transition-all",
          active
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/40",
        )}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className={cn(
              "inline-block h-3 w-3 rounded-full ring-2 ring-inset transition-all",
              active
                ? "bg-primary ring-primary"
                : "bg-transparent ring-muted-foreground/40",
            )}
            aria-hidden
          />
          <span className="font-semibold text-base">{title}</span>
        </div>
        <p className="text-sm font-medium mt-1">{tagline}</p>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          {body}
        </p>
      </Card>
    </button>
  );
}
